import { useState } from 'react'
import { supabase } from '../lib/supabase'

export default function SupabaseDebug() {
  const [results, setResults] = useState({})
  const [testing, setTesting] = useState(false)

  const runDiagnostics = async () => {
    setTesting(true)
    const testResults = {}

    // Test 1: Check environment variables
    testResults.envCheck = {
      supabaseUrl: import.meta.env.VITE_SUPABASE_URL ? 'Set' : 'Missing',
      supabaseKey: import.meta.env.VITE_SUPABASE_ANON_KEY ? 'Set' : 'Missing',
      urlValid: import.meta.env.VITE_SUPABASE_URL?.includes('supabase') || false
    }

    // Test 2: Basic connection test
    try {
      console.log('🔍 Testing basic Supabase connection...')
      const { data, error } = await supabase.from('user_profiles').select('count').limit(1)
      testResults.connectionTest = {
        success: !error,
        error: error?.message || null,
        data: data
      }
    } catch (err) {
      testResults.connectionTest = {
        success: false,
        error: err.message,
        type: err.name
      }
    }

    // Test 3: Auth status
    try {
      const { data: { session }, error } = await supabase.auth.getSession()
      testResults.authTest = {
        success: !error,
        hasSession: !!session,
        error: error?.message || null
      }
    } catch (err) {
      testResults.authTest = {
        success: false,
        error: err.message
      }
    }

    // Test 4: Network connectivity to Supabase
    try {
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
      if (supabaseUrl) {
        const response = await fetch(`${supabaseUrl}/rest/v1/`, {
          method: 'HEAD',
          headers: {
            'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY || '',
            'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY || ''}`
          }
        })
        testResults.networkTest = {
          success: response.ok,
          status: response.status,
          statusText: response.statusText
        }
      } else {
        testResults.networkTest = {
          success: false,
          error: 'No Supabase URL configured'
        }
      }
    } catch (err) {
      testResults.networkTest = {
        success: false,
        error: err.message,
        type: err.name
      }
    }

    setResults(testResults)
    setTesting(false)
  }

  const renderTestResult = (testName, result) => {
    const success = result.success
    return (
      <div key={testName} className="border rounded-lg p-4 mb-4">
        <div className="flex items-center gap-2 mb-2">
          <span className={`text-lg ${success ? 'text-green-600' : 'text-red-600'}`}>
            {success ? '✅' : '❌'}
          </span>
          <h3 className="font-semibold">{testName}</h3>
        </div>
        <pre className="text-xs bg-gray-100 p-2 rounded overflow-auto">
          {JSON.stringify(result, null, 2)}
        </pre>
      </div>
    )
  }

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <div className="bg-white rounded-lg shadow-lg p-6">
        <h1 className="text-2xl font-bold mb-4">Supabase Connection Diagnostics</h1>
        
        <button
          onClick={runDiagnostics}
          disabled={testing}
          className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 disabled:opacity-50 mb-6"
        >
          {testing ? 'Running Tests...' : 'Run Diagnostics'}
        </button>

        {Object.keys(results).length > 0 && (
          <div>
            <h2 className="text-xl font-semibold mb-4">Test Results:</h2>
            {renderTestResult('Environment Variables', results.envCheck)}
            {renderTestResult('Supabase Connection', results.connectionTest)}
            {renderTestResult('Authentication', results.authTest)}
            {renderTestResult('Network Connectivity', results.networkTest)}
            
            <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded">
              <h3 className="font-semibold text-blue-800 mb-2">Troubleshooting Tips:</h3>
              <ul className="text-sm text-blue-700 space-y-1">
                <li>• If environment variables are missing, you need to set VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY</li>
                <li>• If connection fails, check your Supabase project URL and API key</li>
                <li>• If network test fails, there might be connectivity issues or invalid credentials</li>
                <li>• Check browser console for additional error details</li>
              </ul>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
